import { useState } from 'react'
import { Button, HStack, IconButton, Menu, Portal, Text, VStack } from '@chakra-ui/react'
import { FiMoreHorizontal } from 'react-icons/fi'
import { ResultStatus } from '../../../types/script'
import type { TurfContactDto } from '../../../types/canvasser'
import { ConfirmDeceased } from './ConfirmDeceased'
import { lastVisitHint } from './WhoAnsweredStep'

export type DoorAction = 'refused' | 'language' | 'moved' | 'deceased' | 'doesntLiveHere'

const SHOWN = 4
const fullName = (c: TurfContactDto) => `${c.firstName} ${c.lastName ?? ''}`.trim()

function menuFor(allowed: ReadonlySet<ResultStatus>): { value: DoorAction; label: string }[] {
  const items: { value: DoorAction; label: string; needs: ResultStatus }[] = [
    { value: 'refused', label: 'Refused at door', needs: ResultStatus.Refused },
    { value: 'language', label: 'Language barrier', needs: ResultStatus.LanguageBarrier },
    { value: 'moved', label: 'Moved', needs: ResultStatus.Moved },
    { value: 'deceased', label: 'Deceased', needs: ResultStatus.Deceased },
    { value: 'doesntLiveHere', label: "Doesn't live here", needs: ResultStatus.Moved },
  ]
  return items.filter(i => allowed.has(i.needs))
}

/**
 * [Spec §17.2] The one home door screen (household and contact walks): "Who answered?" with a button per
 * listed resident, nobody pre-selected. The canvasser comes back here after each conversation; recorded
 * people show their result, and "Finish this door" rolls up the rest.
 */
export function DoorScreenStep({
  people, recorded, allowed, onTalk, onAction, onNotOnList, onNobodyAnswered, onCouldntKnock, onFinish, isSaving,
}: {
  /** Already ordered: the dock's person (if any) first. */
  people: TurfContactDto[]
  /** contactId → what was recorded ("Talked", "Moved", …). */
  recorded: ReadonlyMap<string, string>
  allowed: ReadonlySet<ResultStatus>
  onTalk: (contactId: string) => void
  onAction: (contactId: string, action: DoorAction) => void
  onNotOnList: () => void
  onNobodyAnswered: () => void
  onCouldntKnock: () => void
  onFinish: () => void
  isSaving: boolean
}) {
  const [showAll, setShowAll] = useState(false)
  const [confirmDeceased, setConfirmDeceased] = useState<TurfContactDto | null>(null)
  const items = menuFor(allowed)
  const shown = showAll ? people : people.slice(0, SHOWN)
  const hidden = people.length - shown.length
  const started = recorded.size > 0

  return (
    <VStack flex={1} align="stretch" gap={3} px={4} pb={6} minH={0}>
      <Text fontSize="xl" fontWeight="bold">Who answered?</Text>
      <VStack align="stretch" gap={2} overflowY="auto" flex={1}>
        {shown.map(p => {
          const done = recorded.get(p.contactId)
          const hint = done ? null : lastVisitHint(p)
          return (
            <HStack key={p.contactId} gap={2}>
              <Button flex={1} minH="56px" justifyContent="flex-start" textAlign="left" variant="outline"
                borderColor="border" bg="surface" disabled={!!done} onClick={() => onTalk(p.contactId)}>
                <VStack align="flex-start" gap={0} flex={1}>
                  <Text fontWeight="semibold" color="fg">{fullName(p)}</Text>
                  {hint && <Text fontSize="sm" color="fg.muted">{hint}</Text>}
                </VStack>
                {done && <Text fontSize="sm" color="fg.muted">{`${done} ✓`}</Text>}
              </Button>
              {!done && items.length > 0 && (
                <Menu.Root positioning={{ placement: 'bottom-end' }}>
                  <Menu.Trigger asChild>
                    <IconButton aria-label={`More for ${fullName(p)}`} variant="ghost" minW="44px" minH="44px">
                      <FiMoreHorizontal />
                    </IconButton>
                  </Menu.Trigger>
                  <Portal>
                    <Menu.Positioner>
                      <Menu.Content>
                        {items.map(i => (
                          <Menu.Item key={i.value} value={i.value} minH="44px"
                            onSelect={() => (i.value === 'deceased' ? setConfirmDeceased(p) : onAction(p.contactId, i.value))}>
                            {i.label}
                          </Menu.Item>
                        ))}
                      </Menu.Content>
                    </Menu.Positioner>
                  </Portal>
                </Menu.Root>
              )}
            </HStack>
          )
        })}
        {hidden > 0 && (
          <Button variant="ghost" minH="44px" alignSelf="flex-start" onClick={() => setShowAll(true)}>
            {`More people… (${hidden})`}
          </Button>
        )}
      </VStack>

      {confirmDeceased && (
        <ConfirmDeceased name={fullName(confirmDeceased)} onCancel={() => setConfirmDeceased(null)} onConfirm={() => {
          const id = confirmDeceased.contactId
          setConfirmDeceased(null)
          onAction(id, 'deceased')
        }} />
      )}

      <Button minH="52px" variant="outline" borderColor="border" onClick={onNotOnList}>Someone not on my list</Button>
      {started ? (
        <Button minH="56px" bg="accent" color="accent.fg" disabled={isSaving} onClick={onFinish}>Finish this door</Button>
      ) : (
        <HStack gap={2}>
          <Button flex={1} minH="48px" variant="ghost" onClick={onNobodyAnswered}>Nobody answered</Button>
          {allowed.has(ResultStatus.Inaccessible) && (
            <Button flex={1} minH="48px" variant="ghost" onClick={onCouldntKnock}>Couldn't knock</Button>
          )}
        </HStack>
      )}
    </VStack>
  )
}
